<script setup>
import {
  computed,
  onMounted,
  useTemplateRef,
  ref,
  getCurrentInstance,
} from 'vue';
import Icon from 'next/icon/Icon.vue';
import Avatar from 'next/avatar/Avatar.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { downloadFile } from '@chatwoot/utils';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';

const { attachment, plain, senderName, senderAvatarUrl } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  showTranscribedText: {
    type: Boolean,
    default: true,
  },
  // Renders without its own surface, for callers that already provide one
  // (message bubbles paint the player with the bubble colour, like WhatsApp).
  plain: {
    type: Boolean,
    default: false,
  },
  // Shown while idle, in the spot the playback speed takes over once the audio
  // starts — like WhatsApp.
  senderName: {
    type: String,
    default: '',
  },
  senderAvatarUrl: {
    type: String,
    default: '',
  },
});

defineOptions({
  inheritAttrs: false,
});

const timeStampURL = computed(() => {
  return timeStampAppendedURL(attachment.dataUrl);
});

const TRANSCRIPT_PREVIEW_LENGTH = 200;
const isTranscriptExpanded = ref(false);
const isTranscriptLong = computed(
  () => (attachment.transcribedText?.length || 0) > TRANSCRIPT_PREVIEW_LENGTH
);
const displayedTranscript = computed(() => {
  const text = attachment.transcribedText || '';
  if (!isTranscriptLong.value || isTranscriptExpanded.value) return text;
  return `${text.slice(0, TRANSCRIPT_PREVIEW_LENGTH).trimEnd()}…`;
});

const audioPlayer = useTemplateRef('audioPlayer');

const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackSpeed = ref(1);

const { uid } = getCurrentInstance();

const WAVEFORM_BAR_COUNT = 44;
const PLAYBACK_SPEEDS = [1, 1.5, 2, 3];

// A stable pseudo waveform per attachment: decoding every voice note in the
// list to draw real peaks would download all of them upfront.
const waveformBars = computed(() => {
  let seed = Number(attachment.id) || attachment.dataUrl?.length || 1;
  return Array.from({ length: WAVEFORM_BAR_COUNT }, () => {
    seed = (seed * 1103515245 + 12345) % 2147483648;
    return 25 + (seed % 75);
  });
});

const progress = computed(() => {
  if (!duration.value) return 0;
  return currentTime.value / duration.value;
});

const hasStarted = computed(() => isPlaying.value || currentTime.value > 0);

// MediaRecorder-produced WebM/Opus blobs lack a Duration header → <audio>.duration
// resolves to Infinity until we seek past the end, which forces the engine to
// scan the file and compute the real length. Safe no-op for files with a real
// duration already (mp3/m4a/etc).
const resolveStreamingDuration = () => {
  const el = audioPlayer.value;
  if (!el) return;
  const onTimeUpdate = () => {
    el.removeEventListener('timeupdate', onTimeUpdate);
    el.currentTime = 0;
    duration.value = el.duration;
  };
  el.addEventListener('timeupdate', onTimeUpdate);
  try {
    el.currentTime = Number.MAX_SAFE_INTEGER;
  } catch {
    el.removeEventListener('timeupdate', onTimeUpdate);
  }
};

const onLoadedMetadata = () => {
  const d = audioPlayer.value?.duration;
  if (!Number.isFinite(d)) {
    resolveStreamingDuration();
    return;
  }
  duration.value = d;
};

const playbackSpeedLabel = computed(() => {
  return `${playbackSpeed.value}x`;
});

// There maybe a chance that the audioPlayer ref is not available
// When the onLoadMetadata is called, so we need to set the duration
// value when the component is mounted
onMounted(() => {
  const d = audioPlayer.value?.duration;
  if (Number.isFinite(d)) duration.value = d;
  audioPlayer.value.playbackRate = playbackSpeed.value;
});

// Listen for global audio play events and pause if it's not this audio
useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    isPlaying.value = false;
  }
});

const formatTime = time => {
  if (!time || Number.isNaN(time)) return '0:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Like WhatsApp: the total length while idle, the elapsed time once it plays.
const displayedTime = computed(() =>
  formatTime(currentTime.value || duration.value)
);

const onTimeUpdate = () => {
  currentTime.value = audioPlayer.value?.currentTime;
};

const seek = event => {
  const time = Number(event.target.value);
  audioPlayer.value.currentTime = time;
  currentTime.value = time;
};

const playOrPause = () => {
  if (isPlaying.value) {
    audioPlayer.value.pause();
    isPlaying.value = false;
  } else {
    // Emit event to pause all other audio
    emitter.emit('pause_playing_audio', uid);
    audioPlayer.value.play();
    isPlaying.value = true;
  }
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  playbackSpeed.value = 1;
  audioPlayer.value.playbackRate = 1;
};

const changePlaybackSpeed = () => {
  const currentIndex = PLAYBACK_SPEEDS.indexOf(playbackSpeed.value);
  const nextIndex = (currentIndex + 1) % PLAYBACK_SPEEDS.length;
  playbackSpeed.value = PLAYBACK_SPEEDS[nextIndex];
  audioPlayer.value.playbackRate = playbackSpeed.value;
};

const downloadAudio = async () => {
  const { fileType, dataUrl, extension } = attachment;
  downloadFile({ url: dataUrl, type: fileType, extension });
};
</script>

<template>
  <audio
    ref="audioPlayer"
    controls
    class="hidden"
    playsinline
    @loadedmetadata="onLoadedMetadata"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
  >
    <source :src="timeStampURL" />
  </audio>
  <div
    v-bind="$attrs"
    class="flex flex-col gap-1.5 w-72 sm:w-[22rem] max-w-full"
    :class="
      plain
        ? ''
        : 'rounded-xl p-2 bg-n-alpha-white border border-n-container shadow-[0px_2px_8px_0px_rgba(94,94,94,0.06)]'
    "
  >
    <div class="flex items-center gap-2.5">
      <button
        v-if="hasStarted"
        class="grid text-sm font-medium border-0 rounded-full shrink-0 size-11 place-content-center bg-n-alpha-2 hover:bg-n-alpha-3"
        :aria-label="$t('CONVERSATION.AUDIO_PLAYER.PLAYBACK_SPEED')"
        @click="changePlaybackSpeed"
      >
        {{ playbackSpeedLabel }}
      </button>
      <Avatar
        v-else-if="senderName || senderAvatarUrl"
        :name="senderName"
        :src="senderAvatarUrl"
        :size="44"
        rounded-full
        class="shrink-0"
      />
      <span
        v-else
        class="grid rounded-full shrink-0 size-11 place-content-center bg-n-alpha-2"
      >
        <Icon icon="i-ph-microphone-fill" class="size-5 text-n-slate-11" />
      </span>
      <button
        class="grid p-0 bg-transparent border-0 shrink-0 size-10 place-content-center"
        :aria-label="
          isPlaying
            ? $t('CONVERSATION.AUDIO_PLAYER.PAUSE')
            : $t('CONVERSATION.AUDIO_PLAYER.PLAY')
        "
        @click="playOrPause"
      >
        <Icon
          class="size-7"
          :icon="isPlaying ? 'i-ph-pause-fill' : 'i-ph-play-fill'"
        />
      </button>
      <div class="relative flex items-center flex-1 h-11 min-w-0">
        <div class="flex items-center w-full h-8 gap-[2px] pointer-events-none">
          <span
            v-for="(height, index) in waveformBars"
            :key="index"
            class="flex-1 rounded-full bg-current"
            :class="
              index / WAVEFORM_BAR_COUNT < progress
                ? 'opacity-90'
                : 'opacity-30'
            "
            :style="{ height: `${height}%` }"
          />
        </div>
        <span
          v-if="duration"
          class="absolute rounded-full pointer-events-none size-3.5 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-n-brand"
          :style="{ left: `${progress * 100}%` }"
        />
        <input
          type="range"
          min="0"
          step="0.01"
          :max="duration || 0"
          :value="currentTime"
          :aria-label="$t('CONVERSATION.AUDIO_PLAYER.SEEK')"
          class="absolute inset-0 w-full h-full bg-transparent appearance-none opacity-0 cursor-pointer"
          @input="seek"
        />
      </div>
    </div>

    <div
      class="flex items-center justify-between gap-3 text-sm text-n-slate-11"
    >
      <span class="tabular-nums">{{ displayedTime }}</span>
      <div class="flex items-center gap-2">
        <slot name="meta" />
        <button
          class="grid p-0 bg-transparent border-0 shrink-0 size-5 place-content-center"
          :aria-label="$t('CONVERSATION.AUDIO_PLAYER.DOWNLOAD')"
          @click="downloadAudio"
        >
          <Icon class="size-4" icon="i-lucide-download" />
        </button>
      </div>
    </div>

    <div
      v-if="attachment.transcribedText && showTranscribedText"
      class="w-full p-3 text-sm break-words rounded-lg text-n-slate-12 bg-n-alpha-1"
    >
      {{ displayedTranscript }}
      <button
        v-if="isTranscriptLong"
        class="block p-0 mt-1 font-medium bg-transparent border-0 text-n-slate-11 hover:text-n-slate-12"
        @click="isTranscriptExpanded = !isTranscriptExpanded"
      >
        {{
          isTranscriptExpanded
            ? $t('CONVERSATION.VOICE_CALL.TRANSCRIPT_SHOW_LESS')
            : $t('CONVERSATION.VOICE_CALL.TRANSCRIPT_SHOW_MORE')
        }}
      </button>
    </div>
  </div>
</template>
